import React, { FunctionComponent, useState } from 'react'
import styled from '@emotion/styled'
import { trimSlashes } from '../../../../utils/links'
import { NavigationCta, NavigationItem } from '../types'
import { Button } from '../../../atoms/button'
import { Text } from '../../../atoms/text'
import PopoverButton from '../../../molecules/popover-button'
import { PopoverItemButton } from '../shared/styles'
import NavigationAction from '../shared/navigation-action'
import DropdownButton from '../../../molecules/dropdown-button'
import { useLocation } from '@reach/router'
import { Link } from 'gatsby'

interface MobileItemListProps {
  items: NavigationItem[]
  ctaButton: NavigationCta
}

const MobileItemList: FunctionComponent<MobileItemListProps> = ({ items, ctaButton }) => {
  const location = useLocation()
  const pathname = location.pathname
  const sanitizedPathname = trimSlashes(pathname)

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const handleClose = () => {
    setIsPopoverOpen(false)
  }

  return (
    <PopoverButton
      isOpen={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      trigger={({ open }) => (
        <>
          {open ? (
            <MenuButton as="span" leadingIcon="close" variant="ghost" gap="m" leadingIconConfig={{ size: '20px' }} />
          ) : (
            <MenuButton as="span" leadingIcon="burger2" variant="ghost" gap="m">
              <Text fontStyle="label-l">Menu</Text>
            </MenuButton>
          )}
        </>
      )}
      config={{ offset: 10, x: 0 }}
      blurConfig={{ isBlurEnabled: true, blurStyles: { top: 'var(--navigation-height)' } }}
    >
      <PopoverContent>
        <ul>
          {items.map((item, index) => {
            return (
              <li key={`${item.title}_${index}`} role="none">
                {!('dropdownItems' in item) && (
                  <PopoverItemButton
                    variant="ghost"
                    as={Link}
                    to={`${item.link}`}
                    role="menuitem"
                    aria-current={trimSlashes(item.link ?? '') === sanitizedPathname ? 'page' : undefined}
                    data-active={`${trimSlashes(item.link ?? '') === sanitizedPathname}` || undefined}
                    onClick={handleClose}
                  >
                    <Text fontStyle="body-l">{item.title}</Text>
                  </PopoverItemButton>
                )}
                {'dropdownItems' in item && (
                  <DropdownButton
                    trigger={
                      <PopoverItemButton
                        as="div"
                        variant="ghost"
                        trailingIcon="chevron-down"
                        gap="full"
                        data-active={
                          `${item.dropdownItems.some(child => trimSlashes(child.link ?? '') === sanitizedPathname)}` ||
                          undefined
                        }
                      >
                        <Text fontStyle="body-l">{item.title}</Text>
                      </PopoverItemButton>
                    }
                  >
                    <DropDownContent>
                      {item.dropdownItems.map((dropdownItem, dropdownIndex) => {
                        return (
                          <li key={`${dropdownItem.title}_${dropdownIndex}`} role="none">
                            <PopoverItemButton
                              variant="ghost"
                              as={Link}
                              to={`${dropdownItem.link}`}
                              onClick={handleClose}
                              role="menuitem"
                              aria-current={
                                trimSlashes(dropdownItem.link ?? '') === sanitizedPathname ? 'page' : undefined
                              }
                              data-active={`${trimSlashes(dropdownItem.link ?? '') === sanitizedPathname}` || undefined}
                              trailingIcon="right-arrow"
                              gap="full"
                              trailingIconConfig={{ size: '20px' }}
                            >
                              <Text fontStyle="body-m">{dropdownItem.title}</Text>
                            </PopoverItemButton>
                          </li>
                        )
                      })}
                    </DropDownContent>
                  </DropdownButton>
                )}
              </li>
            )
          })}
        </ul>
        <NavigationAction {...ctaButton} />
      </PopoverContent>
    </PopoverButton>
  )
}

const MenuButton = styled(Button)`
  width: auto;
  padding-right: 0;
  padding-block: 0.75rem;

  & > p {
    font-weight: var(--medium);
  }
`

const PopoverContent = styled.div`
  width: 100%;
  padding: 1.5rem 0 1.25rem;

  background-color: var(--dark-background-color);
  border-top: 1px solid var(--beige-300, #cecabd);

  & > ul {
    list-style: none;
  }
`

const DropDownContent = styled.ul`
  width: 100%;
  background-color: var(--beige-50, #f8f4e9);
  list-style: none;
  padding: 0.625rem 1rem;

  a {
    width: 100%;
    margin: 0;
    padding-block: 0.875rem;
    border: none;
  }
`

export { MobileItemList }
